import { useCallback, useMemo, useState } from "react";
import { SPINESIM_MODALITIES } from "../classes/enums/MedicalImagesEnum";
import {
  getCurrentModalityViews,
  getClosedViewsForModality,
  filterClosedViews,
  isViewClosingCheck,
} from "../classes/utils/CheckViewUtils";
import { CHECK_CONSTANTS } from "../classes/constants/checkConstants";

/**
 * Custom hook to manage modality view state and interactions in Check module
 * @param {Object} params - Hook parameters
 * @returns {Object} View management state and functions
 */
const useCheckViewManagement = ({
  selectedTwin,
  selectedModality,
  setSelectedModality,
  areViewsReady,
  groupedThumbnailsStandard,
  twinId,
  digitalTwinSurgeryCache,
  checkSessionVersion,
  instancesByModality,
}) => {
  // View closing state for each modality
  const [closedDxViews, setClosedDxViews] = useState(new Set());
  const [closedCtViews, setClosedCtViews] = useState(new Set());
  const [closedMrViews, setClosedMrViews] = useState(new Set());
  const [closedCtVolumeViews, setClosedCtVolumeViews] = useState(new Set());
  const [closedMrVolumeViews, setClosedMrVolumeViews] = useState(new Set());
  const [closingViews, setClosingViews] = useState(new Set());

  // Computed: Get current modality views from cache
  const currentModalityViews = useMemo(() => {
    return getCurrentModalityViews({
      selectedTwin,
      selectedModality,
      areViewsReady,
      groupedThumbnailsStandard,
      twinId,
      digitalTwinSurgeryCache,
      checkSessionVersion,
    });
  }, [
    selectedTwin,
    selectedModality,
    areViewsReady,
    groupedThumbnailsStandard,
    twinId,
    digitalTwinSurgeryCache,
    checkSessionVersion,
  ]);

  // Computed: Get closed views for current modality
  const closedViewsForModality = useMemo(() => {
    return getClosedViewsForModality(selectedModality, {
      closedDxViews,
      closedCtViews,
      closedMrViews,
      closedCtVolumeViews,
      closedMrVolumeViews,
    });
  }, [
    selectedModality,
    closedDxViews,
    closedCtViews,
    closedMrViews,
    closedCtVolumeViews,
    closedMrVolumeViews,
  ]);

  // Computed: Filtered views (excluding closed ones)
  const filteredViews = useMemo(() => {
    return filterClosedViews(currentModalityViews, closedViewsForModality);
  }, [currentModalityViews, closedViewsForModality]);

  // Callback: Check if a view is currently closing
  const isViewClosing = useCallback(
    (viewId, instanceId, index, viewType) => {
      return isViewClosingCheck(viewId, instanceId, index, viewType, closingViews);
    },
    [closingViews]
  );

  // Callback: Handle modality selection
  const handleSelectModality = useCallback((_index, modality) => {
    // Update parent's selected modality state
    setSelectedModality(modality);

    // Clear closed views for the selected modality
    switch (modality) {
      case SPINESIM_MODALITIES.DX:
        setClosedDxViews(new Set());
        break;
      case SPINESIM_MODALITIES.CT:
        setClosedCtViews(new Set());
        break;
      case SPINESIM_MODALITIES.MR:
        setClosedMrViews(new Set());
        break;
      case SPINESIM_MODALITIES.CT_VOLUME:
        setClosedCtVolumeViews(new Set());
        break;
      case SPINESIM_MODALITIES.MR_VOLUME:
        setClosedMrVolumeViews(new Set());
        break;
      default:
        break;
    }
  }, [setSelectedModality]);

  // Callback: Handle view close with animation
  const handleImageClose = useCallback(
    (instanceId, viewId, index, viewType) => {
      const viewKey = viewId || instanceId || `index-${index}-${viewType}`;

      setClosingViews(prev => {
        const newSet = new Set(prev);
        newSet.add(viewKey);
        return newSet;
      });

      setTimeout(() => {
        switch (viewType) {
          case SPINESIM_MODALITIES.DX:
            setClosedDxViews(prev => {
              const newSet = new Set(prev);
              newSet.add(viewId || instanceId);
              return newSet;
            });
            break;
          case SPINESIM_MODALITIES.CT:
            setClosedCtViews(prev => {
              const newSet = new Set(prev);
              newSet.add(viewId || instanceId);
              return newSet;
            });
            break;
          case SPINESIM_MODALITIES.MR:
            setClosedMrViews(prev => {
              const newSet = new Set(prev);
              newSet.add(viewId || instanceId);
              return newSet;
            });
            break;
          case SPINESIM_MODALITIES.CT_VOLUME:
            setClosedCtVolumeViews(prev => {
              const newSet = new Set(prev);
              newSet.add(viewId || instanceId);
              return newSet;
            });
            break;
          case SPINESIM_MODALITIES.MR_VOLUME:
            setClosedMrVolumeViews(prev => {
              const newSet = new Set(prev);
              newSet.add(viewId || instanceId);
              return newSet;
            });
            break;
          default:
            break;
        }

        setClosingViews(prev => {
          const newSet = new Set(prev);
          newSet.delete(viewKey);
          return newSet;
        });
      }, CHECK_CONSTANTS.VIEW_CLOSE_ANIMATION_TIMEOUT);
    },
    []
  );

  // Callback: Handle 3D view toggle
  const handle3dToggle = useCallback(
    (setIs3dViewActive, groupedThumbnailsStandard) => {
      setIs3dViewActive(prevState => {
        const newState = !prevState;

        if (newState) {
          const hasCtVolume = instancesByModality?.CT?.length > 0;
          const hasMrVolume = instancesByModality?.MR?.length > 0;

          if (
            selectedModality === SPINESIM_MODALITIES.CT &&
            hasCtVolume
          ) {
            handleSelectModality(null, SPINESIM_MODALITIES.CT_VOLUME);
          } else if (
            selectedModality === SPINESIM_MODALITIES.MR &&
            hasMrVolume
          ) {
            handleSelectModality(null, SPINESIM_MODALITIES.MR_VOLUME);
          } else if (hasCtVolume) {
            handleSelectModality(null, SPINESIM_MODALITIES.CT_VOLUME);
          } else if (hasMrVolume) {
            handleSelectModality(null, SPINESIM_MODALITIES.MR_VOLUME);
          }
        } else {
          if (selectedModality === SPINESIM_MODALITIES.CT_VOLUME) {
            handleSelectModality(null, SPINESIM_MODALITIES.CT);
            return newState;
          }

          if (selectedModality === SPINESIM_MODALITIES.MR_VOLUME) {
            handleSelectModality(null, SPINESIM_MODALITIES.MR);
            return newState;
          }

          const hasDx = groupedThumbnailsStandard.some(
            group => group.modality === SPINESIM_MODALITIES.DX
          );
          const hasCt = groupedThumbnailsStandard.some(
            group => group.modality === SPINESIM_MODALITIES.CT
          );
          const hasMr = groupedThumbnailsStandard.some(
            group => group.modality === SPINESIM_MODALITIES.MR
          );

          if (hasCt) {
            handleSelectModality(null, SPINESIM_MODALITIES.CT);
          } else if (hasMr) {
            handleSelectModality(null, SPINESIM_MODALITIES.MR);
          } else if (hasDx) {
            handleSelectModality(null, SPINESIM_MODALITIES.DX);
          }
        }
        return newState;
      });
    },
    [instancesByModality, selectedModality, handleSelectModality]
  );

  return {
    // State
    closedDxViews,
    closedCtViews,
    closedMrViews,
    closedCtVolumeViews,
    closedMrVolumeViews,
    closingViews,

    // Computed values
    currentModalityViews,
    closedViewsForModality,
    filteredViews,

    // Functions
    isViewClosing,
    handleSelectModality,
    handleImageClose,
    handle3dToggle,
  };
};

export default useCheckViewManagement;
